<script lang="ts">
    import { isLoadingPalette, palette } from "$lib/image/palette";
    import { Color } from "$lib/utility/color";
    import { COMMANDS } from "$lib/commands";
    import ColorPicker from "$lib/utility/ColorPicker.svelte";
    import { Vec2 } from "@ceyper-common";
    import { EditorSettings } from "$lib/commands/settings";
    import Icon from "$lib/utility/Icon.svelte";

    const { active, index, primary } = palette;

    function updateColor() {
        $index = -1;
    }

    function addColor() {
        //todo command
        $active.colors.push($primary);
        COMMANDS.invoke("editor.tool.palette-index", $active.colors.length - 1);
    }

    $: locked = $EditorSettings["lock-palette"].value;

    let dragging = -1;
    let draggingTarget: Vec2 | null = null;
    let draggingDelete = false;
    function reorderStart(e: PointerEvent) {
        const target = e.target as HTMLElement;
        const index = Array.prototype.indexOf.call(target.parentNode!.children, target);
        COMMANDS.invoke("editor.tool.palette-index", index);

        if (locked) {
            return;
        }

        const style = getComputedStyle(paletteElement);
        const gap = parseFloat(style.gap);
        const size = target.offsetWidth;

        target.setPointerCapture(e.pointerId);

        window.addEventListener("pointermove", reorderMove);
        window.addEventListener("pointerup", reorderEnd);
        window.addEventListener("pointercancel", reorderEnd);

        function reorderMove(e: PointerEvent) {
            dragging = index;
            const pos = Vec2.sub(Vec2.fromEvent(e), Vec2.fromBoundsPosition(paletteElement), Vec2.fromPadding(paletteElement), Vec2.c(-gap / 2));

            const columns = style.getPropertyValue("grid-template-columns").split(" ").length;
            const rows = Math.ceil($active.colors.length / columns);
            let lastRowColumns = $active.colors.length % columns;

            let column = Math.floor(pos.x / (size + gap));
            let row = Math.floor(pos.y / (size + gap));

            draggingDelete = false;
            if (lastRowColumns === 0) {
                if (row === rows) {
                    if (column === 0) {
                        draggingDelete = true;
                    }
                }
                lastRowColumns = columns;
            } else {
                if (row === rows - 1) {
                    if (column === lastRowColumns) {
                        draggingDelete = true;
                    }
                }
            }

            row = Math.clamp(row, 0, rows - 1);
            column = Math.clamp(column, 0, (row === rows - 1) ? lastRowColumns - 1 : (columns - 1));

            draggingTarget = Vec2.c(column, row);
        }

        function reorderEnd() {
            window.removeEventListener("pointermove", reorderMove);
            window.removeEventListener("pointerup", reorderEnd);

            if (!draggingTarget) return;

            const columns = style.getPropertyValue("grid-template-columns").split(" ").length;

            //todo to command
            const colors = $active.colors;
            const color = colors.splice(dragging, 1)[0];

            let index = draggingTarget!.y * columns + draggingTarget!.x;
            if (!draggingDelete) {
                colors.splice(index, 0, color);
            } else {
                index = colors.length - 1;
            }
            $active.colors = colors;

            COMMANDS.invoke("editor.tool.palette-index", index);
            dragging = -1;
            draggingTarget = null;
            draggingDelete = false;
        }
    }

    let paletteElement: HTMLDivElement;

    function keyDown(e: KeyboardEvent) {
        if (!e.key.startsWith("Arrow")) {
            return;
        }

        e.preventDefault();

        const colors = $active.colors.length;
        const gridComputedStyle = window.getComputedStyle(paletteElement);
        const columns = gridComputedStyle.getPropertyValue("grid-template-columns").split(" ").length;
        const rows = Math.ceil(colors / columns);

        let column = $index % columns;
        let row = Math.floor($index / columns);

        if (e.key === "ArrowLeft") {
            column = (column - 1 + columns) % columns;
            if (row === rows - 1 && column >= colors % columns) {
                column = colors % columns - 1;
            }
        } else if (e.key === "ArrowRight") {
            column = (column + 1) % columns;
            if (row === rows - 1 && column >= colors % columns) {
                column = 0;
            }
        }

        if (e.key === "ArrowUp") {
            row = (row - 1 + rows) % rows;
            if (row === rows - 1 && column >= colors % columns) {
                row = rows - 2;
            }
        } else if (e.key === "ArrowDown") {
            row = (row + 1) % rows;
            if (row === rows - 1 && column >= colors % columns) {
                row = 0;
            }
        }

        COMMANDS.invoke("editor.tool.palette-index", row * columns + column);
    }

    function toggleLock() {
        $EditorSettings["lock-palette"].value = !$EditorSettings["lock-palette"].value;
        EditorSettings.save();
    }
</script>

<!----------------------------------------------------------------------------->

<div class="palette" bind:this={paletteElement} style="--drag-column: {(draggingTarget?.x ?? 0) + 1}; --drag-row: {(draggingTarget?.y ?? 0) + 1}" draggable="false">
    {#if $isLoadingPalette}
        <div class="loading">
            loading...
        </div>
    {/if}

    {#each $active.colors as color, i}
        <div
            role="button"
            tabindex="0"
            class="color"
            class:active={i === $index}
            class:dragging={i === dragging}
            style="color: {Color.asHex(color)}"
            on:keydown={keyDown}
            on:pointerdown={reorderStart}
            draggable="false"
        />
    {/each}

    {#if !locked}
        {#if draggingTarget}
            <button class="color delete icon" class:hover={draggingDelete} style="color: var(--g50)"/>
        {:else if $index === -1}
            <button class="color active plus" style="color: {Color.asHex($primary)}" on:click={addColor} />
        {/if}
    {/if}

    <button class="color lock" style="color: var(--g50)" on:click={toggleLock}>
        {#if locked}
            <Icon id="lock-24"/>
        {:else}
            <Icon id="unlock-24"/>
        {/if}
    </button>
</div>

<!----------------------------------------------------------------------------->

<style>
    .palette {
        position: relative;
        display: grid;
        grid-template-columns: repeat(auto-fill, 2em);
        gap: 2px;
        padding: 0.5em 0 0.5em 0.5em;

        &:has(.loading) > *:not(.loading) {
            opacity: 0.125;
        }
    }

    .loading {
        position: absolute;
        inset: 0;
        display: grid;
        place-items: center;
    }

    .color {
        all: unset;
        background-color: currentColor;
        display: block;
        aspect-ratio: 1;
        border: 2px solid var(--g100);
    }

    .active {
        margin: 0;
        border-color: white;
    }

    .dragging {
        grid-column: var(--drag-column);
        grid-row: var(--drag-row);
        margin: -2px;
    }

    button.color:hover {
        cursor: pointer;
    }

    .plus {
        position: relative;
    }

    .plus::before, .plus::after {
        content: "";
        position: absolute;
        inset: 0;

        --border: 0px;
        --thickness: calc(3px + var(--border));
        --color: var(--t100);
        display: block;
        width: 100%;
        height: 100%;

        background-image: linear-gradient(var(--color) 0px, var(--color) 0px),
        linear-gradient(var(--color) 0px, var(--color) 0px), none;
        background-position: center;
        background-size: calc(60% + var(--border)) var(--thickness),
        var(--thickness) calc(60% + var(--border));
        background-repeat: no-repeat;
    }

    .plus::before {
        --color: var(--g50);
        --border: 2px;
    }

    .delete {
        position: relative;

        &::before {
            content: "";
            position: absolute;
            inset: 4px;

            background: white;
            mask: url(/icons/delete-16.svg);
            mask-size: contain;
        }

        &.hover::before {
            background: red;
        }
    }

    .lock {
        grid-column: -2; /* why does this work? */
        padding: 2px;
        display: flex;
        &>div{
            color: var(--t400);
        }

        &:hover>div {
            color: white;
        }
    }
</style>
